// Copy for /our-work/data (Data Maturity — the Stratodata Stairway page).
// Step names come from ./stairway.ts (shared with Data Warehousing and /how-it-works).

const en = {
  seoTitle: 'Data Maturity | Stratodata',
  seoDescription:
    'A 6-step framework that maps your journey from Excel spreadsheets to AI-powered retail operations. See where you are today and what the next step unlocks.',
  hero: {
    title: 'The Stratodata Stairway',
    subtitle: '6 levels of data maturity. Each step lifts your profit per employee.',
    note: 'Most companies are stuck on steps 1–2. We take you to 6.',
    cta: 'Find your level',
  },
  mission: 'Mission: Make this world data driven.',
  stairway: {
    title: 'Where are you on the stairway?',
    subtitle: 'Click a step to see what it looks like — and what it takes to move up.',
    logoAlt: 'Stratodata',
    baseline: 'Profit / employee rises at every step.',
  },
  /** Per-step copy, index 0 = level 1. Titles come from stairwayCopy.stepNames. */
  steps: [
    {
      subtitle: 'Manual data work',
      description:
        'Spreadsheets everywhere. Copy-paste reporting. Hours lost every week reconciling numbers that never match. Sound familiar?',
      symptoms: ['Reports take days to compile', 'No single source of truth', 'Decisions based on gut feeling'],
    },
    {
      subtitle: 'Siloed tools',
      description:
        'You\'ve adopted tools — ERP, e-commerce, marketing platforms — but each lives in its own universe. Data exists, it just doesn\'t talk.',
      symptoms: ['Multiple dashboards, zero alignment', 'IT bottleneck for every report', 'Marketing and finance disagree on revenue'],
    },
    {
      // Steps 3 and 4 show the concrete state as their heading; the stairway name moves to the subtitle (also the graph label)
      title: 'Integrated DWH & BI',
      subtitle: 'Single Source of Truth',
      description:
        'A proper data warehouse connects all your sources. One number, one truth. Your team finally looks at the same reality.',
      symptoms: ['Automated data pipelines', 'Self-service dashboards', 'Consistent KPIs across departments'],
    },
    {
      title: 'Increasing adoption',
      subtitle: 'Data-Driven Culture',
      description:
        'Data stops being an IT project. Every department uses insights daily. Decisions are evidence-based, not opinion-based.',
      symptoms: ['Weekly data reviews across teams', 'North Star KPI tracked company-wide', 'Data literacy growing at every level'],
    },
    {
      subtitle: 'Data start to work',
      description:
        'Your data doesn\'t just inform — it acts. Automated pricing, bidding, forecasting. Humans set strategy, machines execute.',
      symptoms: ['Auto-pricing adjusts daily', 'PPC bids optimise overnight', 'Forecasts update without human touch'],
    },
    {
      subtitle: '"Talk-to-your-data"',
      description:
        'The pinnacle. Your team converses with data in natural language. AI doesn\'t replace your people — it gives them superpowers.',
      symptoms: ['Natural language queries on live data', 'AI agents monitoring anomalies 24/7', 'Strategic recommendations generated daily'],
    },
  ],
  journey: {
    eyebrow: 'The Journey',
    title: 'From spreadsheets to AI — one deliberate step at a time',
  },
  cta: {
    title: 'Ready for the Next Step?',
    text: 'Whether you\'re on step 1 or step 4, we start where you are and take you higher.',
    button: 'Let\'s talk about your next step',
  },
  deliver: {
    title: 'What we deliver at each level',
    subtitle: 'Concrete outcomes, not vague promises.',
    levelPrefix: 'Level',
    cards: [
      { level: '1 → 2', title: 'Escape Excel', what: 'Audit existing data landscape. Identify quick wins. Connect first critical source to a proper pipeline.' },
      { level: '2 → 3', title: 'Build the Foundation', what: 'Cloud data warehouse setup. Automated ETL pipelines. First unified dashboards across ERP, e-commerce & marketing.' },
      { level: '3 → 4', title: 'Drive Adoption', what: 'North Star KPI framework. Department-specific views. Training & enablement workshops for your whole team.' },
      { level: '4 → 5', title: 'Automate Decisions', what: 'Auto-pricing engine. Automated bidding & PPC. Product recommender. Autoforecaster. SBA automation.' },
      { level: '5 → 6', title: 'Unlock AI', what: 'Context-engineered data model for LLMs. Natural language analytics. In-app chatbot. MCP connections to Claude, Copilot & more.' },
      { level: 'Ongoing', title: 'Stay There', what: 'Continuous monitoring, pipeline health, model retraining, data quality audits. We don\'t just build — we run.' },
    ],
  },
};

const sk: typeof en = {
  seoTitle: 'Dátová zrelosť | Stratodata',
  seoDescription:
    'Rámec v 6 krokoch, ktorý ukáže vašu cestu od excelových tabuliek k retailu poháňanému AI. Zistite, kde ste dnes a čo vám prinesie ďalší krok.',
  hero: {
    title: 'Stratodata Stairway',
    subtitle: '6 úrovní dátovej zrelosti. Každý krok zvyšuje váš zisk na zamestnanca.',
    note: 'Väčšina firiem zostane na 1. alebo 2. kroku. My vás dovedieme až na 6.',
    cta: 'Zistite svoju úroveň',
  },
  mission: 'Naša misia: svet, ktorý sa riadi dátami.',
  stairway: {
    title: 'Na ktorom schode stojíte vy?',
    subtitle: 'Kliknite na krok a pozrite sa, ako vyzerá a čo treba urobiť, aby ste sa posunuli vyššie.',
    logoAlt: 'Stratodata',
    baseline: 'Zisk na zamestnanca rastie s každým krokom.',
  },
  steps: [
    {
      subtitle: 'Ručná práca s dátami',
      description:
        'Všade tabuľky a reporty skladané cez copy-paste. Každý týždeň strávite hodiny zosúlaďovaním čísel, ktoré aj tak nesedia. Poznáte to?',
      symptoms: ['Zostavenie reportu trvá dni', 'Chýba jeden zdroj pravdy', 'Rozhoduje sa podľa pocitu'],
    },
    {
      subtitle: 'Izolované nástroje',
      description:
        'Zaviedli ste nástroje ako ERP, e-shop či marketingové platformy, no každý žije vo vlastnom svete. Dáta máte, len spolu nehovoria.',
      symptoms: ['Veľa dashboardov, nulová zhoda', 'Na každý report čakáte na IT', 'Marketing a financie sa nezhodnú na tržbách'],
    },
    {
      title: 'Integrovaný DWH a BI',
      subtitle: 'Jeden zdroj pravdy',
      description:
        'Poriadny dátový sklad prepojí všetky vaše zdroje. Všetci pracujú s tým istým číslom a celý tím sa konečne pozerá na rovnakú realitu.',
      symptoms: ['Automatizované dátové toky', 'Self-service dashboardy', 'Jednotné KPI naprieč oddeleniami'],
    },
    {
      title: 'Dáta sa ujímajú',
      subtitle: 'Dátová kultúra',
      description:
        'Dáta už nie sú projekt IT oddelenia. Každé oddelenie s nimi pracuje denne a rozhoduje sa podľa faktov, nie podľa názorov.',
      symptoms: ['Každý tím raz týždenne prejde dáta', 'North Star metriku sleduje celá firma', 'Dátová gramotnosť rastie na všetkých úrovniach'],
    },
    {
      subtitle: 'Dáta začínajú pracovať',
      description:
        'Dáta už len neinformujú, ale aj konajú. Nacenenie, bidovanie a predikcie bežia automaticky. Ľudia určujú stratégiu a stroje ju vykonávajú.',
      symptoms: ['Ceny sa automaticky upravujú každý deň', 'PPC bidy sa optimalizujú cez noc', 'Predikcie sa aktualizujú bez zásahu človeka'],
    },
    {
      subtitle: '„Rozprávajte sa so svojimi dátami“',
      description:
        'Vrchol schodiska. Váš tím sa s dátami rozpráva prirodzeným jazykom. AI vašich ľudí nenahradí, dá im superschopnosti.',
      symptoms: ['Otázky v prirodzenom jazyku nad živými dátami', 'AI agenti nepretržite strážia anomálie', 'Strategické odporúčania každý deň'],
    },
  ],
  journey: {
    eyebrow: 'Cesta',
    title: 'Od tabuliek k AI – premyslene, krok za krokom',
  },
  cta: {
    title: 'Pripravení na ďalší schod?',
    text: 'Či už ste na 1. alebo na 4. schode, začneme tam, kde práve ste, a posunieme vás vyššie.',
    button: 'Porozprávajme sa o vašom ďalšom schode',
  },
  deliver: {
    title: 'Čo prinášame na každej úrovni',
    subtitle: 'Konkrétne výsledky, nie vágne sľuby.',
    levelPrefix: 'Úroveň',
    cards: [
      { level: '1 → 2', title: 'Zbavte sa Excelu', what: 'Pozrieme sa, s akými dátami dnes pracujete, a nájdeme, čo prinesie výsledok najrýchlejšie. Najdôležitejší zdroj napojíme tak, aby sa čísla už neprepisovali ručne.' },
      { level: '2 → 3', title: 'Postavte základy', what: 'Vybudujeme vám dátový sklad v cloude, do ktorého budú dáta z ERP, e-shopu a marketingu tiecť automaticky. Prvé dashboardy ukážu všetky čísla na jednom mieste.' },
      { level: '3 → 4', title: 'Dostaňte dáta do praxe', what: 'Rámec postavený na North Star metrike. Pohľady na mieru pre jednotlivé oddelenia. Školenia a workshopy pre celý tím.' },
      { level: '4 → 5', title: 'Automatizujte rozhodnutia', what: 'Automatické nacenenie, bidovanie v PPC, odporúčanie produktov, prognózy predaja aj sezónne úpravy kampaní (SBA) bežia nad vašimi dátami každý deň. Vy len nastavíte pravidlá a kontrolujete výsledky.' },
      { level: '5 → 6', title: 'Odomknite AI', what: 'Dátový model pre LLM s Context Engineeringom. Analytika v prirodzenom jazyku. Chatbot priamo v aplikácii. Prepojenie cez MCP na Claude, Copilot a ďalšie.' },
      { level: 'Priebežne', title: 'Udržte si to', what: 'Nepretržitý monitoring, dohľad nad dátovými tokmi, pretrénovanie modelov, audity kvality dát. To, čo postavíme, aj prevádzkujeme.' },
    ],
  },
};

export const dataMaturityCopy = { en, sk };
